import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Truck, Route, CheckCircle, MapPin, Package, AlertTriangle } from "lucide-react";
import { useOnRoadReporting } from "./hooks/useOnRoadReporting";
import { useManifiestoState } from "./hooks/useManifiestoState";
import { setSessionMessage } from './hooks/useSessionMessage';
import { StateManager } from "./service/StateManager";
import axios from "../../axiosConfig";
import Swal from 'sweetalert2';
import OnRoadHeader from "./components/OnRoadHeader";
import RemesaDetails from "./components/RemesaDetails";
import PhaseSection from "./components/PhaseSection";
import EventReportingSheet from "./components/EventReportingSheet";
import ActionsSection from "./components/ActionsSection";
import StateTransitionButtons from "./components/StateTransitionButtons";
import Footer from "./components/Footer";
import TimelineModal from "./components/TimelineModal";
import "../../css/OnRoad.css";
import RemesaSelectionModal from "./components/RemesaSelectionModal";

function PrincipalOnRoad() {
    const navigate = useNavigate();

    const [modoForzadoDescargue, setModoForzadoDescargue] = useState(false);
    const [modoForzadoCargue, setModoForzadoCargue] = useState(false);
    const [modoForzadoEnRuta, setModoForzadoEnRuta] = useState(false);
    
    // Datos del localStorage
    const manifiesto = localStorage.getItem('manifiesto');
    const nombreEmpresa = localStorage.getItem('nombreEmpresa');
    const nombreConductor = localStorage.getItem('nombreConductor');
    const placa = localStorage.getItem('placa');

    // Estados para manejar ambos modales
    const [remesaSelectionSheet, setRemesaSelectionSheet] = useState({ 
        open: false, 
        fase: null, 
        evento: null 
    });
    
    const [eventReportSheet, setEventReportSheet] = useState({ 
        open: false, 
        fase: null, 
        evento: null,
        remesasSeleccionadas: null // Datos de remesas seleccionadas
    });

    const [showTimeline, setShowTimeline] = useState(false);
    
    const { isReporting, reportarNormalidad } = useOnRoadReporting();

    // Hook para gestionar el estado del manifiesto
    const { 
        operacionData,
        estadoOperativo: estadoOperativoReal,
        estadosEventosCargue,
        estadosEventosDescargue,
        informacionEstado,
        timelineData,
        refetchDespuesDeEvento,
        tieneGruposCompletosYPendientesEnCargue,
        tieneGruposPendientesLlegadaDescargue,
        loading
    } = useManifiestoState();

    // Calcular si hay grupos pendientes en CARGUE
    const tieneGruposPendientesCargue = useMemo(() => {
        if (!operacionData) return false;
        return StateManager.tieneGruposPendientesEnCargue(operacionData);
    }, [operacionData]);

    // Calcular si hay grupos pendientes en DESCARGUE
    const tieneGruposPendientesDescargue = useMemo(() => {
        if (!operacionData) return false;
        return StateManager.tieneGruposPendientesEnDescargue(operacionData);
    }, [operacionData]);

    // Obtener estadísticas por fase
    const estadisticasFases = useMemo(() => {
        if (!operacionData) return null;
        return StateManager.obtenerEstadisticasPorFase(operacionData);
    }, [operacionData]);

    // Determinar el estado operativo considerando los modos forzados
    const estadoOperativo = useMemo(() => {
        if (modoForzadoEnRuta) {
            return StateManager.ESTADOS_OPERATIVOS.EN_RUTA;
        }
        if (modoForzadoCargue) {
            return StateManager.ESTADOS_OPERATIVOS.CARGUE;
        }
        if (modoForzadoDescargue) {
            return StateManager.ESTADOS_OPERATIVOS.DESCARGUE;
        }
        return estadoOperativoReal;
    }, [modoForzadoEnRuta, modoForzadoCargue, modoForzadoDescargue, estadoOperativoReal]);

    // Determinar si mostrar el botón "Volver a Cargue"
    const mostrarBotonVolverCargue = useMemo(() => {
        // No mostrar si no hay datos
        if (!operacionData) return false;
        
        // Mostrar si hay grupos pendientes en CARGUE y el estado efectivo NO es CARGUE
        // Usar el estado efectivo (incluye modos forzados)
        if (estadoOperativo === StateManager.ESTADOS_OPERATIVOS.CARGUE) {
            return false; // Si ya estamos en CARGUE (efectivo), no mostrar
        }

        // Reusar cálculo ya disponible (más robusto)
        return tieneGruposPendientesCargue;
    }, [operacionData, estadoOperativo, tieneGruposPendientesCargue]);

    // Determinar si mostrar el botón "Volver a En Ruta" desde CARGUE
    const mostrarBotonVolverEnRutaDesdeCargue = useMemo(() => {
        if (!operacionData) return false;
        // Mostrar si:
        // 1. El estado operativo efectivo es CARGUE (real o forzado)
        // 2. Hay grupos completados Y pendientes en CARGUE
        const enModoCargue = estadoOperativo === StateManager.ESTADOS_OPERATIVOS.CARGUE || modoForzadoCargue;

        if (!enModoCargue) return false;

        return enModoCargue && 
            tieneGruposCompletosYPendientesEnCargue;
    }, [operacionData, estadoOperativo, modoForzadoCargue, tieneGruposCompletosYPendientesEnCargue]);

    // Determinar si mostrar el botón "Volver a En Ruta" desde DESCARGUE
    const mostrarBotonVolverRuta = useMemo(() => {
        if (!operacionData) return false;
        
        const estadoActual = estadoOperativo; // Usar el ajustado por modos forzados
        
        // Solo mostrar si está en DESCARGUE
        if (estadoActual !== StateManager.ESTADOS_OPERATIVOS.DESCARGUE) {
            return false;
        }

        // Verificar si hay grupos que completaron CARGUE pero no iniciaron DESCARGUE
        const gruposRuta = StateManager.agruparRemesasPorRuta(operacionData.remesas);
        const hayGruposEnRuta = gruposRuta.some(grupo => {
            // Solo considerar remesas activas (sin salida_descargue)
            const remesasActivas = grupo.remesas.filter(r => 
                !String(r.fecha_salida_descargue || '').trim()
            );

            // Si no hay remesas activas en el grupo, no cuenta
            if (remesasActivas.length === 0) return false;

            // Verificar si alguna remesa activa cumple: salió de cargue pero no llegó a descargue
            return remesasActivas.some(remesa => 
                String(remesa.fecha_salida_cargue || '').trim() && 
                !String(remesa.fecha_llegada_descargue || '').trim()
            );
        });

        return hayGruposEnRuta;
    }, [operacionData, estadoOperativo]);

    // Acciones de UI
    const abrirSheet = ({ fase, evento }) => {
        // Validar si se puede reportar el evento
        if (!StateManager.puedeReportarEvento(fase, evento, operacionData)) {
            toast.warning('Este evento no está disponible para reportar en este momento');
            return;
        }
        // Abrir modal de selección de remesas
        setRemesaSelectionSheet({ open: true, fase, evento });
    };

    // Cerrar modal de selección de remesas
    const cerrarRemesaSelection = () => {
        setRemesaSelectionSheet({ open: false, fase: null, evento: null });
    };

    // Confirmar selección de remesas y abrir modal de reporte
    const confirmarSeleccionRemesas = (datosSeleccion) => {
        // Cerrar modal de selección
        setRemesaSelectionSheet({ open: false, fase: null, evento: null });
        
        // Abrir modal de reporte con datos de remesas seleccionadas
        setEventReportSheet({ 
            open: true, 
            fase: remesaSelectionSheet.fase,
            evento: remesaSelectionSheet.evento,
            remesasSeleccionadas: datosSeleccion
        });
    };

    // Cerrar modal de reporte de eventos
    const cerrarEventReportSheet = () => {
        setEventReportSheet({ 
            open: false, 
            fase: null, 
            evento: null,
            remesasSeleccionadas: null
        });
    };

    const onSubmitEvento = async (payload) => {
        try {
            await refetchDespuesDeEvento();

            // Limpiar modos forzados para que el estado real (del backend) se refleje en la UI
            // Se limpia siempre después de revalidar para evitar mantener una vista forzada obsoleta.
            setModoForzadoDescargue(false);
            setModoForzadoCargue(false);
            setModoForzadoEnRuta(false);
            setTimeout(() => {
                const message = payload.resultado?.message || payload.message || 
                            `${payload.evento} ${payload.fase} reportado exitosamente`;
                
                toast.success(message, {
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    position: "top-center"
                });
            }, 100);
        } catch (e) {
            console.error('Error procesando evento:', e);
            toast.error(`Error al procesar respuesta del evento ${payload.evento}`);
            throw new Error(e.message || "No fue posible procesar la respuesta");
        }
    };

    const handleNormalidad = async () => {
        if (isReporting) return;

        try {
            const resultado = await reportarNormalidad('REPORTA NORMALIDAD EN RUTA');
            
            if (!resultado.success) {
                console.error('Error al reportar normalidad:', resultado.message);
            }

        } catch (error) {
            console.error('Error inesperado reportando normalidad:', error);
            toast.error('Error inesperado al enviar normalidad');
        }
    };   

    const handleNovedad = () => {
        navigate('/novedades', {
            state: {
                title: "Reportar Novedad en Ruta",
                label: "Tipo de Novedad",
                tipoMovimiento: "",
                readOnly: false
            }
        });
    };

    const handlePreOperacional = async () => {
		try {
            const tokenOperacion = localStorage.getItem('tokenOperacion');
        
            if (!manifiesto || !tokenOperacion) {
                toast.error('Faltan datos necesarios para obtener el pre-operacional');
                return;
            }
			const response = await axios.get('/obtenerPreOperacional', {
				params: {
					manifiesto: manifiesto,
					referencia: tokenOperacion,
				},
			});

			const { success, message, data } = response.data;
			const newWindow = window.open('', '_blank');
			if (success) {
				const { url } = data;

				if (url) {

					try {
						const validatedUrl = new URL(url, window.location.origin).href;
						newWindow.location.href = validatedUrl;
					} catch (validationError) {
						newWindow.close();
						toast.error(`La URL recibida no es válida: ${url}`);
					}
				} else {
					newWindow.close();
					toast.warning('No se recibió la URL esperada');
				}
			} else {
				newWindow.close();
				toast.error(message || 'Ocurrió un error al procesar la solicitud');
			}
		} catch (error) {
			const errorMessage = error.response?.data?.message || 'Ocurrió un error al procesar la solicitud';
			toast.error(errorMessage);
		}
	};

    const notificarLlegadaDestino = () => {
        if (estadoOperativo !== StateManager.ESTADOS_OPERATIVOS.EN_RUTA) {
            toast.warning('Esta acción solo está disponible en modo EN RUTA');
            return;
        }

        // Limpiar modo forzado 
        setModoForzadoEnRuta(false);
        setModoForzadoCargue(false);
        setModoForzadoDescargue(true);

        const stats = estadisticasFases?.descargue;
        const mensaje = stats 
            ? `Transicionado a Descargue (${stats.completados}/${stats.total} grupos completados)`
            : 'Transicionado a modo Descargue';

        toast.success(mensaje, {
            autoClose: 2000,
            hideProgressBar: false
        });
    };    
    
    const volverAEnRuta = () => {
        // Solo permitir si estamos en DESCARGUE
        if (estadoOperativo !== StateManager.ESTADOS_OPERATIVOS.DESCARGUE) {
            toast.warning('Esta acción solo está disponible en modo DESCARGUE');
            return;
        }

        // Solo permitir si hay grupos pendientes
        if (!tieneGruposPendientesLlegadaDescargue) {
            toast.info('Todos los grupos ya reportaron LLEGADA DESCARGUE');
            return;
        }
        
        setModoForzadoDescargue(false);
        setModoForzadoCargue(false);
        setModoForzadoEnRuta(true);

        const stats = estadisticasFases?.cargue;
        const mensaje = stats 
            ? `Volviendo a En Ruta (${stats.completados}/${stats.total} grupos en cargue completados)`
            : 'Transicionado a modo En Ruta';

        toast.success(mensaje, {
            autoClose: 2000,
            hideProgressBar: false
        });
    };

    // Handler para volver a CARGUE desde EN_RUTA
    const volverACargue = () => {
        if (![StateManager.ESTADOS_OPERATIVOS.EN_RUTA, StateManager.ESTADOS_OPERATIVOS.DESCARGUE].includes(estadoOperativoReal)) {
            toast.warning('Esta acción solo está disponible en modo EN RUTA o DESCARGUE');
            return;
        }

        if (!tieneGruposPendientesCargue) {
            toast.info('No hay grupos pendientes en la fase de Cargue');
            return;
        }

        // Limpiar modo forzado de descargue y activar modo cargue
        setModoForzadoEnRuta(false);
        setModoForzadoDescargue(false);
        setModoForzadoCargue(true);

        const stats = estadisticasFases?.cargue;
        const mensaje = stats 
            ? `Volviendo a Cargue (${stats.pendientes} grupo${stats.pendientes !== 1 ? 's' : ''} pendiente${stats.pendientes !== 1 ? 's' : ''})`
            : 'Transicionado a modo Cargue';

        toast.success(mensaje, {
            autoClose: 2500,
            hideProgressBar: false
        });
    };

    // Handler para volver a EN_RUTA desde CARGUE
    const volverAEnRutaDesdeCargue = () => {
        // Validar que estemos en contexto de CARGUE
        const enContextoCargue = estadoOperativoReal === StateManager.ESTADOS_OPERATIVOS.CARGUE || 
                                modoForzadoCargue;
        
        if (!enContextoCargue) {
            toast.warning('Esta acción solo está disponible en modo CARGUE');
            return;
        }

        if (!tieneGruposCompletosYPendientesEnCargue) {
            toast.info('No hay grupos completados o todos están pendientes');
            return;
        }

        // Limpiar modos forzados
        setModoForzadoCargue(false);
        setModoForzadoDescargue(false);
        setModoForzadoEnRuta(true);

        // FORZAR re-render del estado - OJO: esto se puede optimizar
        // Si hay grupos en descargue, debería transicionar a DESCARGUE
        // const hayGruposEnDescargue = operacionData && 
        //     StateManager.agruparRemesasPorRuta(operacionData.remesas).some(grupo => {
        //         const remesa = grupo.remesas[0];
        //         return remesa.fecha_llegada_descargue && !remesa.fecha_salida_descargue;
        //     });

        // const stats = estadisticasFases?.cargue;
        // const mensaje = hayGruposEnDescargue
        //     ? 'Transicionando a Descargue (hay grupos pendientes de descarga)'
        //     : stats 
        //         ? `Transicionando a En Ruta (${stats.completados} grupo${stats.completados !== 1 ? 's' : ''} completado${stats.completados !== 1 ? 's' : ''})`
        //         : 'Transicionado a modo En Ruta';

        const stats = estadisticasFases?.cargue;
        const mensaje = stats 
            ? `Transicionando a En Ruta (${stats.completados} grupo${stats.completados !== 1 ? 's' : ''} completado${stats.completados !== 1 ? 's' : ''})`
            : 'Transicionado a modo En Ruta';

        toast.success(mensaje, {
            autoClose: 2500,
            hideProgressBar: false
        });

        // Forzar actualización del estado si hay grupos en descargue
        // if (hayGruposEnDescargue) {
        //     setTimeout(() => {
        //         // El estado se recalculará automáticamente en el próximo render
        //         // gracias al useMemo de estadoOperativo
        //     }, 100);
        // }
    };

    const handleSalirOnRoad = () => {
        Swal.fire({
            title: '¿Salir de OnRoad?',
            text: 'Se cerrará la sesión actual y deberá validar sus datos nuevamente.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, salir',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                const tenantEmpresa = localStorage.getItem('tenantEmpresa');
                
                sessionStorage.setItem('salida_manual', 'true');
                setSessionMessage('Sesión cerrada exitosamente', 'success');
                
                const keysToRemove = [
                    'manifiesto', 
                    'nombreEmpresa', 
                    'conductor', 
                    'nombreConductor', 
                    'ruta', 
                    'ciudadOrigen', 
                    'ciudadDestino', 
                    'placa', 
                    'tokenConductor', 
                    'tokenOperacion',   
                    'reporteSalida', 
                    'reporteLlegada', 
                    'modoEstatico'
                ];
                
                keysToRemove.forEach(key => localStorage.removeItem(key));
                
                // Limpiar datos específicos del manifiesto
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('resumen_') || 
                        key.startsWith('timeline_') || 
                        key.startsWith('novedades_')) {
                        localStorage.removeItem(key);
                    }
                });
                
                navigate(`/onroad/${tenantEmpresa}`, { replace: true });
            }
        });
    };

    // Mostrar loading mientras se cargan los datos
    if (loading) {
        return (
            <div className="onroad-page">
                <div className="onroad-main-content">
                    <OnRoadHeader
                        nombreEmpresa={nombreEmpresa}
                        manifiesto={manifiesto}
                        conductor={nombreConductor}
                        placa={placa}
                    />
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Cargando datos del manifiesto...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onroad-page">
            <div className="onroad-main-content">
                <OnRoadHeader
                    nombreEmpresa={nombreEmpresa}
                    manifiesto={manifiesto}
                    conductor={nombreConductor}
                    placa={placa}
                />

                <div className="onroad-container">
                    <div className="main-content-container">
                        <RemesaDetails 
                            operacionData={operacionData} 
                            informacionEstado={informacionEstado}
                            key={`operacion-${operacionData?.id || 'loading'}`}
                        />
                        <ActionsSection
                            onNormalidad={handleNormalidad}
                            onNovedad={handleNovedad}
                            onPreOperacional={handlePreOperacional}
                            loadingNormalidad={isReporting}
                            estadoOperativo={estadoOperativo}
                        />

                        <StateTransitionButtons
                            onLlegadaDestino={notificarLlegadaDestino}
                            onVolverEnRuta={volverAEnRuta}
                            onVolverACargue={volverACargue}  
                            onVolverEnRutaDesdeCargue={volverAEnRutaDesdeCargue}
                            estadoOperativo={estadoOperativo} 
                            mostrarBotonVolverCargue={mostrarBotonVolverCargue} 
                            mostrarBotonVolverEnRutaDesdeCargue={mostrarBotonVolverEnRutaDesdeCargue}
                            mostrarBotonVolverRuta={mostrarBotonVolverRuta}
                        />

                        
                        <div className="content-sections">
                            {estadoOperativo === StateManager.ESTADOS_OPERATIVOS.CARGUE && (
                                <>
                                    {/* NUEVO: Mostrar aviso si está en modo forzado */}
                                    {modoForzadoCargue && tieneGruposPendientesCargue && (
                                        <div className="phase-notice cargue-notice">
                                            <div className="notice-icon">📦</div>
                                            <div className="notice-content">
                                                <h4>Grupos Pendientes en Cargue</h4>
                                                <p>
                                                    {estadisticasFases?.cargue.pendientes} grupo{estadisticasFases?.cargue.pendientes !== 1 ? 's' : ''} 
                                                    {' '}necesita{estadisticasFases?.cargue.pendientes === 1 ? '' : 'n'} completar eventos de cargue
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <PhaseSection
                                        fase={StateManager.FASES.CARGUE}
                                        ultimos={estadosEventosCargue}
                                        onReport={abrirSheet}
                                    />
                                </>
                            )}

                            {estadoOperativo === StateManager.ESTADOS_OPERATIVOS.DESCARGUE && (
                                <>
                                    {/* NUEVO: Mostrar aviso si está en modo forzado */}
                                    {modoForzadoDescargue && tieneGruposPendientesDescargue && (
                                        <div className="phase-notice descargue-notice">
                                            <div className="notice-icon">🚚</div>
                                            <div className="notice-content">
                                                <h4>Grupos en Descargue</h4>
                                                <p>
                                                    {estadisticasFases?.descargue.pendientes} grupo{estadisticasFases?.descargue.pendientes !== 1 ? 's' : ''} 
                                                    {' '}pendiente{estadisticasFases?.descargue.pendientes === 1 ? '' : 's'} de descargue
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <PhaseSection
                                        fase={StateManager.FASES.DESCARGUE}
                                        ultimos={estadosEventosDescargue}
                                        onReport={abrirSheet}
                                    />
                                </>
                            )}

                            {estadoOperativo === StateManager.ESTADOS_OPERATIVOS.EN_RUTA && (
                                <div className="phase-section">
                                    <div className="phase-title-simple">
                                        <Route size={22} />
                                        <span>En Ruta</span>
                                    </div>
                                    
                                    <div className="en-ruta-info">
                                        {/* NUEVO: Mostrar estadísticas de progreso */}
                                        {estadisticasFases && (
                                            <div className="ruta-progress-cards">
                                                <div className="progress-card cargue">
                                                    <div className="progress-card-header">
                                                        <Package size={18} />
                                                        <span>Fase Cargue</span>
                                                    </div>
                                                    <div className="progress-card-stats">
                                                        <span className="stat-value">
                                                            {estadisticasFases.cargue.completados}/{estadisticasFases.cargue.total}
                                                        </span>
                                                        <span className="stat-label">Grupos completados</span>
                                                    </div>
                                                    {estadisticasFases.cargue.pendientes > 0 && (
                                                        <div className="progress-card-warning">
                                                            <AlertTriangle size={14} />
                                                            <span>{estadisticasFases.cargue.pendientes} pendiente{estadisticasFases.cargue.pendientes !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="progress-card descargue">
                                                    <div className="progress-card-header">
                                                        <MapPin size={18} />
                                                        <span>Fase Descargue</span>
                                                    </div>
                                                    <div className="progress-card-stats">
                                                        <span className="stat-value">
                                                            {estadisticasFases.descargue.completados}/{estadisticasFases.descargue.total}
                                                        </span>
                                                        <span className="stat-label">grupos completados</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="en-ruta-status-card">
                                            <div className="status-icon">
                                                <MapPin size={24} className="text-primary" />
                                            </div>
                                            <div className="status-content">
                                                <h4>Vehículo en Tránsito</h4>
                                                <p>Use las acciones disponibles arriba para reportar novedades o gestionar el estado de cargue/descargue.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {estadoOperativo === StateManager.ESTADOS_OPERATIVOS.COMPLETADO && (
                                <div className="phase-section">
                                    <div className="completion-message">
                                        <CheckCircle size={48} className="text-success" />
                                        <h3>Manifiesto Completado</h3>
                                        <p>Todas las remesas han sido procesadas exitosamente</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="timeline-section">
                            <button 
                                className="timeline-button"
                                onClick={() => setShowTimeline(true)}
                            >
                                <Truck size={20} />
                                Ver Flujo del Manifiesto
                            </button>

                            <button 
                                className="exit-onroad-button"
                                onClick={handleSalirOnRoad}
                            >
                                Salir de OnRoad
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {/* Modales */}
            <RemesaSelectionModal
                isOpen={remesaSelectionSheet.open}
                onClose={cerrarRemesaSelection}
                onConfirm={confirmarSeleccionRemesas}
                operacionData={operacionData}
                fase={remesaSelectionSheet.fase}
                evento={remesaSelectionSheet.evento}
            />

            <EventReportingSheet
                isOpen={eventReportSheet.open}
                onClose={cerrarEventReportSheet}
                fase={eventReportSheet.fase || StateManager.FASES.CARGUE}
                evento={eventReportSheet.evento || StateManager.EVENTOS.LLEGADA}
                manifiesto={manifiesto}
                placa={placa}
                onSubmit={onSubmitEvento}
                operacionData={operacionData}
                remesasSeleccionadas={eventReportSheet.remesasSeleccionadas}
            />

            <TimelineModal
                open={showTimeline}
                onClose={() => setShowTimeline(false)}
                manifiesto={manifiesto}
                timelineData={timelineData}
            />

            <ToastContainer 
                position="top-center" 
                autoClose={2200}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
}

export default PrincipalOnRoad;